// 010A — estado, resumen para FHIR y `Par[]` del PDF. El catálogo y las decisiones están en `catalogo010ADatos.ts`.
import { conCodigoCie, paresDeCampos, valoresVacios, type Valores } from './camposSimplesDatos';
import type { DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import type { Par } from './index';
import {
  CAMPOS_MICROBIOLOGIA_010A,
  CAMPOS_SERVICIO_010A,
  CAMPOS_TB_AFECTADO_010A,
  CAMPOS_TB_ANTECEDENTES_010A,
  CAMPOS_TB_MUESTRA_010A,
  CAMPOS_TB_SOLICITUD_010A,
  CAMPOS_TEXTOS_LIBRES_010A,
  CAMPOS_TOMA_MUESTRA_010A,
  CAMPOS_VIH_ITS_010A,
  claveLibre,
  EXAMENES_CON_TEXTO_010A,
  GRUPOS_EXAMENES_010A,
  LIBRES_010A,
  type GrupoExamenes,
} from './catalogo010ADatos';

/** Clave de la casilla de un examen: `<grupo>:<posición>` (las etiquetas se repiten entre grupos). */
export const claveExamen = (grupo: string, indice: number): string => `${grupo}:${indice}`;

export interface Estado010A {
  readonly diagnosticos: readonly DiagnosticoCie10Valor[];
  readonly servicio: Valores;
  readonly examenes: Valores;
  /** Filas libres (espacios en blanco del papel) por grupo, por `claveLibre`. */
  readonly libres: Valores;
  readonly microbiologia: Valores;
  readonly textos: Valores;
  readonly tomaMuestra: Valores;
  readonly vihIts: Valores;
  readonly tbAfectado: Valores;
  readonly tbAntecedentes: Valores;
  readonly tbMuestra: Valores;
  readonly tbSolicitud: Valores;
}

export function estadoInicial010A(): Estado010A {
  return {
    diagnosticos: [0, 1].map(() => ({ texto: '', cie10: null })),
    servicio: valoresVacios(CAMPOS_SERVICIO_010A),
    examenes: {},
    libres: {},
    microbiologia: valoresVacios(CAMPOS_MICROBIOLOGIA_010A),
    textos: valoresVacios(CAMPOS_TEXTOS_LIBRES_010A),
    tomaMuestra: valoresVacios(CAMPOS_TOMA_MUESTRA_010A),
    vihIts: valoresVacios(CAMPOS_VIH_ITS_010A),
    tbAfectado: valoresVacios(CAMPOS_TB_AFECTADO_010A),
    tbAntecedentes: valoresVacios(CAMPOS_TB_ANTECEDENTES_010A),
    tbMuestra: valoresVacios(CAMPOS_TB_MUESTRA_010A),
    tbSolicitud: valoresVacios(CAMPOS_TB_SOLICITUD_010A),
  };
}

/** Texto libre de un examen de Microbiología ("de ..."): KOH, o los tres renglones del cultivo micótico unidos con " / ". */
function textoDeExamen(e: Estado010A, item: string): string {
  const claves = EXAMENES_CON_TEXTO_010A[item] ?? [];
  return claves.map((c) => (e.microbiologia[c] ?? '').trim()).filter(Boolean).join(' / ');
}

/**
 * Exámenes pedidos de un grupo: las casillas marcadas, los de Microbiología con texto libre "de ..." aunque la casilla no se haya marcado (el texto
 * no se pierde) y las filas libres del papel escritas a mano.
 */
export function examenesMarcados(e: Estado010A, grupo: GrupoExamenes): string[] {
  const impresos = grupo.items.flatMap((item, i) => {
    const de = textoDeExamen(e, item);
    if (e.examenes[claveExamen(grupo.clave, i)] !== 'true' && !de) return [];
    return [de ? `${item} de ${de}` : item];
  });
  const libres = Array.from({ length: LIBRES_010A[grupo.clave] ?? 0 }, (_, n) => (e.libres[claveLibre(grupo.clave, n)] ?? '').trim()).filter(Boolean);
  return [...impresos, ...libres];
}

const GRUPO_MICROBIOLOGIA = GRUPOS_EXAMENES_010A.find((g) => g.clave === 'microbiologia')!;
const posicionMicrobiologia = (item: string): number => GRUPO_MICROBIOLOGIA.items.indexOf(item);

/** Cambia los textos de Microbiología; al escribir el "de ..." de KOH / cultivo micótico se marca su casilla (escribirlo es pedirlo). */
export function conTextosMicrobiologia(e: Estado010A, cambios: Valores): Estado010A {
  const microbiologia = { ...e.microbiologia, ...cambios };
  const examenes = { ...e.examenes };
  for (const item of Object.keys(EXAMENES_CON_TEXTO_010A)) {
    if (textoDeExamen({ ...e, microbiologia }, item)) examenes[claveExamen('microbiologia', posicionMicrobiologia(item))] = 'true';
  }
  return { ...e, microbiologia, examenes };
}

/** Casilla mostrada: marcada, o con texto "de ..." escrito (KOH / cultivo micótico), que cuenta como pedido y no se borra al desmarcar. */
export function examenMarcado(e: Estado010A, grupo: string, indice: number): boolean {
  const item = grupo === 'microbiologia' ? GRUPO_MICROBIOLOGIA.items[indice] : undefined;
  return e.examenes[claveExamen(grupo, indice)] === 'true' || (item !== undefined && textoDeExamen(e, item) !== '');
}

export function conExamenMarcado(e: Estado010A, grupo: string, indice: number, marcado: boolean): Estado010A {
  return { ...e, examenes: { ...e.examenes, [claveExamen(grupo, indice)]: marcado ? 'true' : '' } };
}

/** `ServiceRequest.code.text`: "Grupo: a, b — Grupo: c" (lo muestra `LabsTab` tal cual). */
export function textoSolicitud010A(e: Estado010A): string {
  const partes = GRUPOS_EXAMENES_010A.map((g) => ({ g, m: examenesMarcados(e, g) }))
    .filter((x) => x.m.length > 0)
    .map((x) => `${x.g.etiqueta}: ${x.m.join(', ')}`);
  return partes.join(' — ') || 'Solicitud de laboratorio';
}

export function prioridadFhir010A(prioridad: string | undefined): 'urgent' | 'routine' {
  return prioridad === 'Urgente' ? 'urgent' : 'routine';
}

export function paresDe010A(e: Estado010A): Par[] {
  const dx = e.diagnosticos.map((d, i) => ({ pregunta: `Diagnóstico ${i + 1}`, respuesta: conCodigoCie(d) }));
  return [
    ...dx,
    ...paresDeCampos(CAMPOS_SERVICIO_010A, e.servicio),
    ...GRUPOS_EXAMENES_010A.map((g) => ({ pregunta: g.etiqueta, respuesta: examenesMarcados(e, g).join(', ') })),
    ...paresDeCampos(CAMPOS_MICROBIOLOGIA_010A.slice(0, 2), e.microbiologia),
    ...paresDeCampos(CAMPOS_TEXTOS_LIBRES_010A, e.textos),
    ...paresDeCampos(CAMPOS_TOMA_MUESTRA_010A, e.tomaMuestra),
    ...paresDeCampos(CAMPOS_VIH_ITS_010A, e.vihIts, 'VIH/ITS — '),
    ...paresDeCampos(CAMPOS_TB_AFECTADO_010A, e.tbAfectado, 'Tuberculosis, tipo de afectado — '),
    ...paresDeCampos(CAMPOS_TB_ANTECEDENTES_010A, e.tbAntecedentes, 'Tuberculosis, antecedentes — '),
    ...paresDeCampos(CAMPOS_TB_MUESTRA_010A, e.tbMuestra, 'Tuberculosis, tipo de muestra — '),
    ...paresDeCampos(CAMPOS_TB_SOLICITUD_010A, e.tbSolicitud, 'Tuberculosis, solicitud — '),
  ].filter((p) => p.respuesta.trim() !== '');
}
